import {   useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {  createInventory, createRole, getRoleDetail, getRoles, updateRole, deleteRole } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetRoles = (tenantId = null) => {
  const {data, isLoading, isError, error, isSuccess} = useQuery({
    queryKey: ['roles', tenantId],
    queryFn: () => getRoles(tenantId),
  });

  return {data, isLoading, isSuccess, isError, error};
};

export const useGetRoleDetail = (id) => {
  const {data, isLoading, isError, error, isSuccess} = useQuery({
    queryKey: ['role',id],
    queryFn: () => getRoleDetail(id),
  });

  return {data, isLoading, isSuccess, isError, error};
};

export const useCreateInventory = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data) => createInventory(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      // Optionally handle success, e.g., refetch user profiles
    },
    onError: (error) => {
      // Optionally handle error
      console.error("Error creating tenant admin:", error);
    },
  });

  return mutation;
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (data) => createRole(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      
      // Show success notification
      showNotification('success', 'Role created successfully!');
      
      // Navigate immediately without setTimeout
      navigate('/roles');
    },
    onError: (error) => {
      console.error("Error creating role:", error);
      
      // Show error notification
      let errorMessage = 'Error creating role. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: ({ id, data }) => updateRole(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      
      // Show success notification
      showNotification('success', 'Role updated successfully!');
      
      // Navigate immediately without setTimeout
      navigate('/roles');
    },
    onError: (error) => {
      console.error("Error updating role:", error);
      
      // Show error notification
      let errorMessage = 'Error updating role. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (id) => deleteRole(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      
      // Show success notification
      showNotification('success', 'Role deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting role:", error);
      
      // Show error notification
      let errorMessage = 'Error deleting role. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};